import { lazy, Suspense } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import { useUIStore } from '@/stores/useUIStore';
import { useAuthStore } from '@/stores/useAuthStore';
import { usePermissions } from '@/hooks/usePermissions';

/**
 * The panel (and with it the streaming logic and message rendering) is a
 * separate chunk: most sessions never open the assistant, so it should not sit
 * in the dashboard's critical path.
 */
const AssistantPanel = lazy(() =>
  import('./AssistantPanel').then((module) => ({ default: module.AssistantPanel })),
);

function useAssistantAccess(): boolean {
  const isAuthenticated = useAuthStore((state) => state.status === 'authenticated');
  const { canViewCompanyHealth } = usePermissions();
  return isAuthenticated && canViewCompanyHealth;
}

/**
 * Launcher for the assistant. It lives in the topbar, in normal flow, so it
 * cannot cover table controls, column menus, or pagination.
 */
export function AssistantLauncher(): JSX.Element | null {
  const open = useUIStore((state) => state.aiAssistantOpen);
  const setOpen = useUIStore((state) => state.setAiAssistantOpen);
  const allowed = useAssistantAccess();

  if (!allowed) return null;

  return (
    <Tooltip title={open ? 'Chiudi assistente' : 'Apri assistente AI'}>
      <IconButton
        color={open ? 'primary' : 'default'}
        onClick={() => setOpen(!open)}
        aria-label={open ? 'Chiudi assistente AI' : 'Apri assistente AI'}
        aria-expanded={open}
        aria-controls="ai-assistant-panel"
      >
        {open ? <CloseIcon /> : <AutoAwesomeIcon />}
      </IconButton>
    </Tooltip>
  );
}

/**
 * Assistant panel. The launcher is rendered separately in the topbar; this
 * component only mounts the panel while it is open.
 */
export function AIAssistant(): JSX.Element | null {
  const open = useUIStore((state) => state.aiAssistantOpen);
  const setOpen = useUIStore((state) => state.setAiAssistantOpen);
  const allowed = useAssistantAccess();

  if (!allowed || !open) return null;

  return (
    <Suspense
      fallback={
        <Box
          sx={{
            position: 'fixed',
            bottom: { xs: 16, md: 24 },
            right: { xs: 16, md: 24 },
            p: 6,
            borderRadius: 3,
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: (theme) => theme.customShadows.xl,
            zIndex: (theme) => theme.zIndex.drawer,
          }}
        >
          <CircularProgress size={24} aria-label="Caricamento assistente" />
        </Box>
      }
    >
      <AssistantPanel onClose={() => setOpen(false)} />
    </Suspense>
  );
}
